import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createStoreUniformForm,
  getStoreUniformForm,
  getStoreUniformEmployeeHistory,
  listStoreUniformEmployees,
  listStoreUniformTypes,
  updateStoreUniformForm,
} from '../../../services/storeUniformFormApi';

const empty = {
  employeeId: '',
  employeeCode: '',
  tempEmployeeId: '',
  permanentEmployeeId: '',
  employeeName: '',
  designation: '',
  gender: '',
  mobile: '',
  uniformType: '',
  size: '',
  quantity: 1,
  reason: '',
  remarks: '',
};

const UNIFORM_SIZES = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const dash = (value) => value || '-';
const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const StoreUniformFormCreateEdit = () => {
  const { id, employeeId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    const loadSelectedEmployee = (items) => {
      if (!employeeId) return Promise.resolve();

      return getStoreUniformEmployeeHistory(employeeId).then((history) => {
        const selected = history.data?.employee;
        if (!selected) return;

        setEmployees((prev) => (prev.some((x) => String(x.id) === String(selected.id)) ? prev : [selected, ...prev]));
        setForm((current) => ({
          ...current,
          employeeId: selected.id,
          employeeCode: selected.employeeCode || '',
          tempEmployeeId: selected.tempEmployeeId || '',
          permanentEmployeeId: selected.permanentEmployeeId || '',
          employeeName: selected.employeeName || selected.name || '',
          designation: selected.designationName || selected.designation || '',
          gender: selected.gender || '',
          mobile: selected.mobile || '',
        }));
      });
    };

    Promise.all([
      listStoreUniformTypes().then((response) => setTypes(response.data || [])),
      listStoreUniformEmployees({ limit: 100 }).then((response) => {
        const items = response.data?.items || response.data || [];
        setEmployees(items);
        return loadSelectedEmployee(items);
      }),
      id
        ? getStoreUniformForm(id).then((response) => setForm({ ...empty, ...response.data, employeeId: response.data.employeeId || '' }))
        : Promise.resolve(),
    ])
      .catch((e) => setError(getErrorMessage(e, employeeId ? 'Unable to load employee uniform details.' : 'Unable to load active employees.')))
      .finally(() => setLoading(false));
  }, [id, employeeId]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleEmployeeChange = (event) => {
    const value = event.target.value;
    const selected = employees.find((item) => String(item.id) === value);

    setForm((current) => ({
      ...current,
      employeeId: value,
      employeeCode: selected?.employeeCode || '',
      tempEmployeeId: selected?.tempEmployeeId || '',
      permanentEmployeeId: selected?.permanentEmployeeId || '',
      employeeName: selected?.employeeName || selected?.name || '',
      designation: selected?.designationName || selected?.designation || '',
      gender: selected?.gender || '',
      mobile: selected?.mobile || '',
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    setError('');
    setSuccessMessage('');

    if (!form.employeeId) {
      setError('Please select an employee.');
      return;
    }

    if (!form.uniformType) {
      setError('Please select a uniform type.');
      return;
    }

    if (!form.size) {
      setError('Please select a uniform size.');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.tempEmployeeId;
      delete payload.permanentEmployeeId;

      if (id) await updateStoreUniformForm(id, payload);
      else await createStoreUniformForm(payload);

      navigate(employeeId ? `/store/uniform-forms/employee/${employeeId}` : '/store/uniform-forms', {
        state: {
          successMessage: id ? 'Uniform request updated successfully.' : 'Uniform request created successfully.',
        },
      });
    } catch (err) {
      setError(getErrorMessage(err, id ? 'Unable to update the uniform request.' : 'Unable to submit the uniform request.'));
      setSaving(false);
    }
  };

  return (
    <div className="card p-4">
      <h3>{id ? 'Edit' : 'Add'} Uniform</h3>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setError('')} />
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccessMessage('')} />
        </div>
      )}
      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />Loading...</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="store-uniform-employee" className="form-label">Employee</label>
              <select id="store-uniform-employee" disabled={!!employeeId} className="form-select" value={form.employeeId || ''} onChange={handleEmployeeChange} required>
                <option value="">Select Employee</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    TEMP: {dash(employee.tempEmployeeId)} | PERM: {dash(employee.permanentEmployeeId)} | {employee.employeeName || employee.name || '-'}
                  </option>
                ))}
              </select>
            </div>

            {[
              ['tempEmployeeId', 'Temporary Employee ID'],
              ['permanentEmployeeId', 'Permanent Employee ID'],
              ['employeeName', 'Employee Name'],
              ['designation', 'Designation'],
              ['gender', 'Gender'],
              ['mobile', 'Mobile'],
            ].map(([key, label]) => (
              <div className="col-md-6" key={key}>
                <label className="form-label" htmlFor={`store-uniform-${key}`}>{label}</label>
                <input id={`store-uniform-${key}`} readOnly className="form-control" name={key} value={dash(form[key])} />
              </div>
            ))}

            <div className="col-md-6">
              <label htmlFor="store-uniform-type" className="form-label">Uniform Type</label>
              <select id="store-uniform-type" required className="form-select" name="uniformType" value={form.uniformType || ''} onChange={handleChange}>
                <option value="">Select</option>
                {types.map((type) => <option key={type.id} value={type.name}>{type.name}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="store-uniform-size" className="form-label">Size</label>
              <select id="store-uniform-size" className="form-select" name="size" value={form.size || ''} onChange={handleChange} required>
                <option value="">Select Uniform Size</option>
                {UNIFORM_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </div>

            {[
              ['quantity', 'Quantity', 'number'],
              ['reason', 'Reason', 'text'],
              ['remarks', 'Remarks', 'text'],
            ].map(([key, label, type]) => (
              <div className="col-md-6" key={key}>
                <label className="form-label" htmlFor={`store-uniform-${key}`}>{label}</label>
                <input id={`store-uniform-${key}`} className="form-control" name={key} type={type} value={form[key] || ''} onChange={handleChange} />
              </div>
            ))}
          </div>
          <button type="submit" className="btn btn-primary mt-3" disabled={saving}>{saving && <span className="spinner-border spinner-border-sm me-2" />}{saving ? 'Saving...' : 'Save Draft'}</button>
          <button type="button" className="btn btn-outline-secondary mt-3 ms-2" onClick={() => navigate('/store/uniform-forms')}>Cancel</button>
        </form>
      )}
    </div>
  );
};

export default StoreUniformFormCreateEdit;
